import {
    Control,
    Controller,
    UseFieldArrayAppend,
    UseFieldArrayRemove,
    UseFieldArrayUpdate,
    UseFormResetField,
} from "react-hook-form";
import EffectiveFees from "./EffectiveFees";
import { FormData } from "./PayrollBreakdownEditor";
import { SalaryDeductionType } from "../../../__generated__/graphql";
import OvertimePaid from "./OvertimePaid";
import VStack from "../../../ui_atoms/VStack";
import Text from "../../Text";
import { useTheme } from "../../../hooks/use-theme";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useMemo } from "react";
import Bonuses from "./Bonuses";

type PayrollBreakdownBonusEffectiveFeesOvertimePaidProps = {
    control: Control<FormData>;
    resetField: UseFormResetField<FormData>;
    bonuses?: {
        onCreate: UseFieldArrayAppend<FormData, "bonuses">;
        onUpdate: UseFieldArrayUpdate<FormData, "bonuses">;
        onDeleteAtIndex: UseFieldArrayRemove;
        clientDeductions: SalaryDeductionType[];
    };
    effectiveFees?: {
        onCreate: UseFieldArrayAppend<FormData, "effectiveFees">;
        onUpdate: UseFieldArrayUpdate<FormData, "effectiveFees">;
        onDeleteAtIndex: UseFieldArrayRemove;
    };
    overtime?: {
        overtimeWorkDelta: number;
        hourlyInvoiceAmount: number;
        formattedEndOfMonthOvertimeTotal: number;
        overtimePaidAtPercent: number;
    };
    timeSheetsNeedCompletion?: boolean;
};
const PayrollBreakdownBonusEffectiveFeesOvertimePaid = ({
    control,
    resetField,
    bonuses,
    effectiveFees,
    overtime,
}: PayrollBreakdownBonusEffectiveFeesOvertimePaidProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const overtimePaidAttributeName = useMemo(() => {
        if (overtime === undefined) {
            return "overtimePaid100";
        }
        if (overtime?.overtimePaidAtPercent === 100) {
            return "overtimePaid100";
        } else if (overtime?.overtimePaidAtPercent === 125) {
            return "overtimePaid125";
        } else {
            throw new Error("Invalid overtimePaidAtPercent value");
        }
    }, [overtime]);
    return (
        <VStack gap={1}>
            {bonuses && (
                <Controller
                    name="bonuses"
                    control={control}
                    render={({ field: { value } }) => {
                        return <Bonuses bonuses={value} {...bonuses} />;
                    }}
                />
            )}
            {effectiveFees && (
                <Controller
                    name="effectiveFees"
                    control={control}
                    render={({ field: { value } }) => {
                        return <EffectiveFees effectiveFees={value} {...effectiveFees} />;
                    }}
                />
            )}
            {overtime?.formattedEndOfMonthOvertimeTotal &&
                Number(overtime.formattedEndOfMonthOvertimeTotal) >= 0 && (
                    <Controller
                        name={overtimePaidAttributeName}
                        control={control}
                        rules={{
                            validate: (value) => {
                                const delta =
                                    overtime.formattedEndOfMonthOvertimeTotal - Number(value);
                                if (delta < 0) {
                                    return i18n.t("overtimePaid.overtime_paid_too_high", {
                                        deltaWorkHours: overtime.formattedEndOfMonthOvertimeTotal,
                                    });
                                }
                                return true;
                            },
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                            return (
                                <>
                                    <OvertimePaid
                                        value={value}
                                        {...overtime}
                                        onCreate={(newWorkDelta) => {
                                            if (newWorkDelta !== 0) {
                                                onChange(newWorkDelta);
                                            } else {
                                                resetField(overtimePaidAttributeName);
                                            }
                                        }}
                                        onClear={() => {
                                            resetField(overtimePaidAttributeName);
                                        }}
                                    />
                                    {error && (
                                        <Text style={{ color: colors.error }}>{error.message}</Text>
                                    )}
                                </>
                            );
                        }}
                    />
                )}
        </VStack>
    );
};

export default PayrollBreakdownBonusEffectiveFeesOvertimePaid;
